/**
 * The Users screen's URL view state and sort columns (DNKADM-0430 P1-A).
 *
 * Split from `MembersList.tsx` so the component file exports components only
 * (`react-refresh/only-export-components`) and so the column rules are testable alone.
 *
 * URL params on `/members` (all optional; a default is never written):
 * - `tab`: `all` (default) | `active` | `inactive` | `invited` | `prospects` (admin only).
 * - `q`: the active tab's search text.
 * - `sort` + `dir`: the roster or Invited sort (roster default Name ascending; Invited
 *   default Invited, newest first). Prospects is server paged and has no sort.
 * - `page`: the Prospects page, 1-indexed.
 * A tab change clears `q`, `sort`, `dir` and `page`, because each tab has its own columns.
 */
import type { UserInvitation, UserRow, UsersStateFilter } from '@/api/users';
import { toTime, type SortState, type SortValue } from '@/components/list/listSort';
import { accountStateBadge } from './accountState';
import { roleBadge } from './roleBadge';

/** The Users-screen tabs: the three roster state filters, Invited, and admin-only Prospects. */
export type UsersTab = UsersStateFilter | 'INVITED' | 'PROSPECTS';

/** Each tab's `tab=` URL value. */
export const USERS_TAB_PARAM: Record<UsersTab, string> = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  INVITED: 'invited',
  PROSPECTS: 'prospects',
};

/**
 * Read the `tab` param. Unknown values read as All Users, and so does `prospects` for a
 * non-admin (the tab is not rendered for them).
 */
export function parseUsersTab(raw: string, isAdmin: boolean): UsersTab {
  const match = (Object.keys(USERS_TAB_PARAM) as UsersTab[]).find(
    (tab) => USERS_TAB_PARAM[tab] === raw,
  );
  if (match === undefined) return 'ALL';
  if (match === 'PROSPECTS' && !isAdmin) return 'ALL';
  return match;
}

/** The roster's state filter for a tab, or `null` for Invited/Prospects. */
export function rosterStateOf(tab: UsersTab): UsersStateFilter | null {
  return tab === 'INVITED' || tab === 'PROSPECTS' ? null : tab;
}

/** Every param the Users screen keeps in the URL, with its default. */
export const USERS_URL_DEFAULTS = { tab: 'all', q: '', sort: '', dir: '', page: '1' };

// --- Roster (All / Active / Inactive) ------------------------------------------------

export type RosterSortKey = 'name' | 'email' | 'role' | 'state';
export const ROSTER_SORT_KEYS: readonly RosterSortKey[] = ['name', 'email', 'role', 'state'];
export const ROSTER_DEFAULT_SORT: SortState<RosterSortKey> = { key: 'name', dir: 'asc' };

/**
 * What a roster row SHOWS in a column, which is what it sorts by: the display name (name,
 * else the user id), the email, the Role badge label, and the Account state badge label.
 * A row with no role renders the placeholder, so it sorts with the blanks (last).
 */
export function rosterSortValue(user: UserRow, key: RosterSortKey): SortValue {
  switch (key) {
    case 'name':
      return user.name ?? user.userId;
    case 'email':
      return user.email;
    case 'role':
      return user.role ? roleBadge(user.role).label : null;
    case 'state':
      return accountStateBadge(user).label;
  }
}

/** The roster search: name, email or user id contains the query (case-insensitive). */
export function rosterMatches(user: UserRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return `${user.name ?? ''} ${user.email ?? ''} ${user.userId}`.toLowerCase().includes(needle);
}

// --- Invited ---------------------------------------------------------------------------

export type InvitedSortKey = 'email' | 'invited' | 'expires';
export const INVITED_SORT_KEYS: readonly InvitedSortKey[] = ['email', 'invited', 'expires'];
export const INVITED_DEFAULT_SORT: SortState<InvitedSortKey> = { key: 'invited', dir: 'desc' };

/** What an invitation row shows (and sorts by) in each sortable column. */
export function invitedSortValue(invitation: UserInvitation, key: InvitedSortKey): SortValue {
  switch (key) {
    case 'email':
      return invitation.email;
    case 'invited':
      return toTime(invitation.createdAt);
    case 'expires':
      return toTime(invitation.expiresAt);
  }
}

/** The Invited search: the email contains the query (case-insensitive). */
export function invitedMatches(invitation: UserInvitation, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (invitation.email ?? '').toLowerCase().includes(needle);
}
